import { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Loader } from "../Loader";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FilterListIcon from "@mui/icons-material/FilterList";
import { getTicketReport } from "../../Provider/parseDataProvider";
import { Box } from "@mui/material";

const TicketReport = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatuses, setSelectedStatuses] = useState({
    new: true,
    in_progress: true,
    resolved: true,
  });
  const [total, setTotal] = useState(0);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const fetchTicketRecords = async () => {
    setLoading(true);
    const ticketCalc = await getTicketReport([]);
    setRawData(ticketCalc.data);
    setTotal(ticketCalc.total);
    setLoading(false);
  };

  useEffect(() => {
    fetchTicketRecords();
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (status) => {
    setSelectedStatuses((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  const statuses = [
    { key: "new", label: "New", color: "#2196f3" },
    { key: "in_progress", label: "In Progress", color: "#FFA000" },
    { key: "resolved", label: "Resolved", color: "#4caf50" },
  ];

  const calculateTicketData = () => {
    return statuses
      .filter((status) => selectedStatuses[status.key])
      .map((status) => ({
        name: status.label,
        value: rawData[status.key] || 0,
        itemStyle: { color: status.color },
      }));
  };

  const pieChartData = calculateTicketData();

  const getChartOption = () => ({
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      formatter: "{b}: {c} ({d}%)",
      backgroundColor: "#1A1A1A",
      borderColor: "rgba(255, 239, 153, 0.20)",
      borderWidth: 1,
      textStyle: {
        color: "#FFF",
      },
    },
    legend: {
      orient: "horizontal",
      bottom: 10,
      left: "center",
      textStyle: {
        color: "#FFF",
      },
    },
    series: [
      {
        name: "Ticket Status",
        type: "pie",
        radius: ["40%", "70%"],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: "#242424",
          borderWidth: 2,
        },
        label: {
          show: true,
          formatter: "{b}: {c}",
          color: "#FFF",
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: "bold",
            color: "#FFD700",
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(255, 215, 0, 0.5)",
          },
        },
        data: pieChartData,
      },
    ],
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        padding: "16px",
        backgroundColor: "#242424",
        borderRadius: "8px",
        border: "1px solid rgba(255, 239, 153, 0.20)",
        marginTop:4
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#FFD700",
            margin: 0,
          }}
        >
          Support Ticket Status Overview
        </h2>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleMenuOpen}
            sx={{
              height: "40px",
              borderRadius: "5px",
              border: "1px solid rgba(255, 239, 153, 0.20)",
              color: "#FFF",
              fontWeight: 400,
              fontSize: "14px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#FFD700",
                backgroundColor: "rgba(255, 215, 0, 0.05)",
              },
            }}
          >
            <FilterListIcon
              sx={{ marginRight: "6px", width: "16px", height: "16px" }}
            />{" "}
            Status
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "status-filter-button",
            }}
            slotProps={{
              paper: {
                sx: {
                  width: "220px",
                  padding: "8px",
                  maxHeight: "300px",
                  bgcolor: "#1A1A1A",
                  border: "1px solid rgba(255, 239, 153, 0.20)",
                },
              },
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {statuses.map((status) => (
              <MenuItem
                key={status.key}
                dense
                onClick={() => handleStatusChange(status.key)}
                sx={{
                  padding: "2px 16px",
                  color: "#FFF",
                  "&:hover": {
                    bgcolor: "rgba(255, 215, 0, 0.15)",
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedStatuses[status.key]}
                      onChange={() => {}}
                      size="small"
                      sx={{
                        color: status.color,
                        "&.Mui-checked": {
                          color: status.color,
                        },
                      }}
                    />
                  }
                  label={status.label}
                  sx={{
                    marginRight: 0,
                    width: "100%",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      color: "#FFF",
                    },
                  }}
                />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        {pieChartData.length > 0 ? (
          <ReactECharts
            option={getChartOption()}
            style={{ height: "400px", width: "100%" }}
            opts={{ renderer: "canvas" }}
          />
        ) : (
          <Box
            sx={{
              height: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <p style={{ color: "#FFF" }}>
              No data to display. Please select at least one status.
            </p>
          </Box>
        )}
      </Box>

      <Box sx={{ marginTop: "16px" }}>
        <p style={{ textAlign: "center", color: "rgba(255, 255, 255, 0.7)" }}>
          Total Selected Support Tickets: {total}
        </p>
      </Box>
    </Box>
  );
};

export default TicketReport;
